<template>
  <div>
    <v-form ref="form" v-show="!isFetchingInfo" class="facility-technical-info-tab">
      <v-card class="pa-5 pa-sm-10" elevation="0">
        <div class="pa-sm-5">
          <v-tabs
                  v-model="currentTab"
                  hide-slider
                  background-color="transparent"
                  class="info-tab"
                  active-class="active-info-tab"
                  show-arrows>
              <v-tab href="#general" tabindex="1">
                  <v-card-subtitle class="pa-2">
                      {{ $t('book/technical-info:tabs.general') }}
                  </v-card-subtitle>
              </v-tab>
              <v-tab href="#elevator" v-show="elevatorTabActive"  tabindex="2">
                  <v-card-subtitle class="pa-2">
                      {{ $t('book/technical-info:tabs.elevator') }}
                  </v-card-subtitle>
              </v-tab>
              <v-tab href="#emergency" v-show="elevatorTabActive"  tabindex="3">
                <v-card-subtitle class="pa-2">
                  {{ $t('book/technical-info:tabs.emergency') }}
                </v-card-subtitle>
              </v-tab>
              <v-tab href="#fire-control" v-show="elevatorTabActive"  tabindex="4">
                  <v-card-subtitle class="pa-2">
                      {{ $t('book/technical-info:tabs.fire-control') }}
                  </v-card-subtitle>
              </v-tab>
              <v-tab href="#escalator" v-show="escalatorOrWalkway"  tabindex="5">
                  <v-card-subtitle class="pa-2">
                      {{
                      escalatorTabActive ?
                          $t('book/technical-info:tabs.escalator') :
                          $t('book/technical-info:tabs.walkway')
                      }}
                  </v-card-subtitle>
              </v-tab>
          </v-tabs>
        </div>
          <v-alert
            v-if="data.reducedShaftHead || data.reducedShaftPit"
            outlined
            type="warning"
            prominent
            border="left"
            color="orange"
          >
              {{ getShaftMineWarningText }}
          </v-alert>
          <v-tabs-items v-model="currentTab">
              <v-tab-item
                      value="general"
                      class="pa-3 pa-sm-5"
                      eager
              >
                  <v-row align="center">
                      <v-col>
                          <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs}">
                                  <v-btn
                                          color="primary"
                                          rounded
                                          elevation="0"
                                          class="mr-3 mb-3 mb-md-0 white--text"
                                          v-bind="attrs"
                                          v-on="on"
                                          :disabled="!code.code"
                                          @click="$router.push(`/${$store.state.manager.bookModuleUrl}/public/qrcode/${code.code}/onboarding`)"
                                          tabindex="6"
                                  >
                                      {{ $t('book/technical-info:btn-onboarding') }}
                                  </v-btn>
                              </template>
                              <span>{{ $t('book/technical-info:onboarding-tooltip') }}</span>
                          </v-tooltip>
                          <p class="greyMedium--text mb-0 mt-3" v-if="!code.code">{{
                              $t('book/technical-info:no-qr-code')
                              }}</p>

                      </v-col>
                      <v-col class="text-right">
                          <p v-if="data.updatedOn" class="greyTint--text">{{ $t('common:last-updated-on') }} {{
                              data.updatedOn
                              }}</p>
                          <p v-if="data.updatedByUsername" class="greyTint--text">{{
                              $t('book/technical-info:last-updated-by')
                              }}
                              {{ data.updatedByUsername }}</p>
                          <p v-if="!data.updatedOn && !data.updatedByUsername" class="scaleOrange--text">
                              {{ $t('book/facility:facility.dashboard.information.state.not-done') }}</p>
                      </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.installation') }}</h5>
                      </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <!-- Installation -->
                          <v-text-field
                                  v-model="data.factoryNumber"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.factory-number')"
                                  tabindex="7"
                          />

                          <v-text-field
                                  v-model="data.model"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.model')"
                                  tabindex="8"
                          />

                          <v-text-field
                                  v-model="data.buildYear"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.build-year')"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  type="number"
                                  tabindex="9"
                          />

                          <DateField
                                  ref="dateModernized"
                                  @update="(v) => (data.modernizedOn = v)"
                                  :isClearable="true"
                                  :label="$t('book/technical-info:labels.date-modernized')"
                                  tabindex="10"
                          />

                          <DateField
                                  ref="datePermit"
                                  @update="(v) => (data.buildingPermit = v)"
                                  :isClearable="true"
                                  :label="$t('book/technical-info:labels.building-permit')"
                                  tabindex="11"
                          />
                      </v-col>
                      <v-col cols="12" md="5">
                          <DateField
                                  ref="datePermission"
                                  @update="(v) => (data.permissionToUse = v)"
                                  :isClearable="true"
                                  :label="$t('book/technical-info:labels.permission-date')"
                                  tabindex="12"
                          />

                          <CustomerTypeAhead
                                  ref="assemblerTypeahead"
                                  :value="data.assembler"
                                  @input="data.assembler = $event"
                                  :label="$t('book/technical-info:labels.assembler')"
                                  tabindex="13"
                          />

                          <v-text-field
                                  v-model="data.unknownAssembler"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.other-assembler')"
                                  tabindex="14"
                          />

                          <DateField
                                  ref="warrantyEnd"
                                  @update="(v) => (data.warrantyEnd = v)"
                                  isClearable
                                  :label="$t('book/technical-info:labels.warranty-end')"
                                  tabindex="15"
                          />
                          <v-text-field
                                  v-model="data.technicalStandard"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.technical-standard')"
                                  tabindex="16"
                          />


                      </v-col>
                  </v-row>
                  <v-row justify="space-between" v-show="elevatorTabActive || escalatorOrWalkway">

                      <v-col cols="12" md="5">
                          <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.key-safe') }}</h5>
                          <v-checkbox
                                  v-model="data.keySafeExists"
                                  :label="$t('book/technical-info:labels.key-safe-exists')"
                                  color="secondary"
                                  tabindex="17"
                          />

                          <v-textarea
                                  v-show="data.keySafeExists"
                                  v-model="data.keySafeInfo"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.key-safe-info')"
                                  tabindex="18"
                          />
                      </v-col>
                      <v-col cols="12" md="5">

                          <!-- Engine Room Section -->
                          <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.engine') }}</h5>
                          <v-checkbox
                                  v-model="data.engineRoomExists"
                                  :label="$t('book/technical-info:labels.engine-room-exists')"
                                  color="secondary"
                                  tabindex="19"
                          />

                          <v-select
                                  v-if="data.engineRoomExists"
                                  v-model="data.engineRoomPosition"
                                  outlined
                                  :label="$t('book/technical-info:labels.engine-room-position')"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  :items="restEngineRoomPositions"
                                  clearable
                                  item-value="name"
                                  item-text="translatedName"
                                  tabindex="20"
                          >
                          </v-select>

                          <v-textarea
                                  v-if="data.engineRoomExists"
                                  v-model="data.engineRoomPositionInfo"
                                  outlined
                                  :label="$t('book/technical-info:labels.engine-room-position-info')"
                                  clearable
                                  tabindex="21"
                          />
                      </v-col>
                  </v-row>
              </v-tab-item>


              <v-tab-item value="elevator" eager>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.tech') }}</h5>
                      </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <!-- Technical informations Section -->

                          <v-select
                                  v-model="data.type"
                                  outlined
                                  :label="$t('book/technical-info:labels.elevator-type')"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  :items="restElevatorType"
                                  clearable
                                  item-value="name"
                                  item-text="translatedName"
                                  tabindex="13"
                          >
                          </v-select>
                          <v-select
                                  v-model="data.drive"
                                  outlined
                                  :label="$t('book/technical-info:labels.drive')"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  :items="restDrives"
                                  clearable
                                  @change="getSubDrive(data.drive)"
                                  item-text="translatedName"
                                  item-value="name"
                                  tabindex="14"
                          >
                          </v-select>

                          <v-select
                                  v-if="data.drive === 'HYDRAULIC'"
                                  v-model="data.hydraulicDriveType"
                                  outlined
                                  :label="$t('book/technical-info:labels.hydraulic-drive')"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  :items="restHydraulicDriveType"
                                  clearable
                                  item-value="name"
                                  item-text="translatedName"
                                  tabindex="15"
                          >
                          </v-select>

                          <v-select
                                  v-else-if="data.drive === 'ROPE'"
                                  v-model="data.ropeDriveType"
                                  outlined
                                  :label="$t('book/technical-info:labels.rope-drive')"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  :items="restRopeDriveType"
                                  clearable
                                  item-value="name"
                                  item-text="translatedName"
                                  tabindex="16"
                          >
                          </v-select>

                          <v-select
                                  v-model="data.doorPosition"
                                  outlined
                                  :label="$t('book/technical-info:labels.door-position')"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  :items="restDoorPositions"
                                  clearable
                                  item-value="name"
                                  item-text="translatedName"
                                  tabindex="17"
                          >
                          </v-select>

                          <v-text-field
                                  v-model="data.maxLoad"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.max-load')"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  type="number"
                                  tabindex="18"
                          />

                          <v-text-field
                                  v-model="data.maxPersons"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.max-persons')"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  type="number"
                                  tabindex="19"
                          />

                          <v-text-field
                                  v-model="data.operatingSpeed"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.operating-speed')"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  type="number"
                                  tabindex="20"
                          />
                          <v-text-field
                                  v-model="data.cabinHeight"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.cabin-height')"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  type="number"
                                  tabindex="21"
                          />
                      </v-col>

                      <v-col cols="12" md="5">
                          <v-text-field
                                  v-model="data.control"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.controls')"
                                  tabindex="22"
                          />

                          <v-text-field
                                  v-model="data.frequencyConverter"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.frequency-converter')"
                                  tabindex="23"
                          />

                          <v-select
                                  v-model="data.doorSecurityType"
                                  outlined
                                  :label="$t('book/technical-info:labels.door-security')"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  :items="restDoorSecurityType"
                                  clearable
                                  item-value="name"
                                  item-text="translatedName"
                                  tabindex="24"
                          >
                          </v-select>

                          <CustomerTypeAhead
                                  ref="manufacturerTypeahead1"
                                  :value="data.manufacturer"
                                  @input="data.manufacturer = $event"
                                  :label="$t('book/technical-info:labels.manufacturer')"
                                  tabindex="25"
                          />

                          <v-text-field
                                  v-model="data.unknownManufacturer"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.other-manufacturer')"
                                  tabindex="26"
                          />

                          <v-text-field
                                  v-model="data.power"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.power')"
                                  tabindex="27"
                          />
                      </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">

                        <!-- cabin section -->
                        <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.cabin') }}</h5>
                        <v-text-field
                            v-model="data.cabinWidth"
                            outlined
                            clearable
                            type="number"
                            :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                            :label="$t('book/technical-info:labels.cabin-width')"
                            tabindex="28"
                        />

                        <v-text-field
                            v-model="data.cabinDepth"
                            outlined
                            clearable
                            type="number"
                            :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                            :label="$t('book/technical-info:labels.cabin-depth')"
                            tabindex="29"
                        />


                        <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.glass-door') }}</h5>
                          <v-checkbox
                              v-model="data.glassDoorPresent"
                              :label="$t('book/technical-info:labels.glass-door-present')"
                              color="secondary"
                              tabindex="30" />
                          <v-select
                            v-if="data.glassDoorPresent"
                            v-model="data.glassDoorType"
                            outlined
                            :label="$t('book/technical-info:labels.glass-door-type')"
                            :menu-props="{ bottom: true, offsetY: true }"
                            :items="restGlassDoorTypes"
                            clearable
                            item-value="name"
                            item-text="translatedName"
                            tabindex="31"
                          />
                      </v-col>

                    <v-col cols="12" md="5">
                      <h6 class="primary--text mb-5">{{ $t('book/technical-info:titles.cabin-door') }}</h6>
                      <v-select
                          v-model="data.cabinDoorType"
                          outlined
                          :label="$t('book/technical-info:labels.cabin-door-type')"
                          :menu-props="{ bottom: true, offsetY: true }"
                          :items="restDoorType"
                          clearable
                          item-value="name"
                          item-text="translatedName"
                          tabindex="32"
                      >
                      </v-select>

                      <v-text-field
                          v-model="data.cabinDoorWidth"
                          outlined
                          clearable
                          type="number"
                          :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                          :label="$t('book/technical-info:labels.cabin-door-width')"
                          tabindex="33"
                      />

                      <v-text-field
                          v-model="data.cabinDoorHeight"
                          outlined
                          clearable
                          type="number"
                          :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                          :label="$t('book/technical-info:labels.cabin-door-height')"
                          tabindex="34"
                      />
                    </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <v-checkbox
                                  v-model="data.reducedShaftHead"
                                  :label="$t('book/technical-info:labels.reduced-shaft-head')"
                                  color="secondary"
                                  tabindex="35"
                          />

                          <v-checkbox
                                  v-model="data.shaftPitEntry"
                                  :label="$t('book/technical-info:labels.shaft-pit-entry')"
                                  color="secondary"
                                  tabindex="36"
                          />

                          <v-checkbox
                                  v-model="data.reducedShaftPit"
                                  :label="$t('book/technical-info:labels.reduced-shaft-pit')"
                                  color="secondary"
                                  tabindex="37"
                          />
                      </v-col>
                      <v-col cols="12" md="5">
                          <v-checkbox
                                  v-model="data.knownOperator"
                                  :label="$t('book/technical-info:labels.known-operator')"
                                  color="secondary"
                                  tabindex="38"
                          />

                          <v-checkbox
                                  v-model="data.awmModule"
                                  :label="$t('book/technical-info:labels.AWM-module')"
                                  color="secondary"
                                  tabindex="39"
                          />
                      </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">


                          <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.story') }}</h5>
                          <v-row>
                              <v-col cols="12" lg="6" md="6" sm="12" xs="12">
                                  <v-text-field
                                          v-model="data.laneStart"
                                          outlined
                                          clearable
                                          :label="$t('book/technical-info:labels.lane-start')"
                                          tabindex="40"
                                  />
                              </v-col>
                              <v-col cols="12" lg="6" md="6" sm="12" xs="12">
                                  <v-text-field
                                          v-model="data.laneEnd"
                                          outlined
                                          clearable
                                          :label="$t('book/technical-info:labels.lane-end')"
                                          tabindex="41"
                                  />
                              </v-col>
                          </v-row>

                          <v-text-field
                                  v-model="data.stations"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.stations')"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  type="number"
                                  tabindex="42"
                          />

                          <v-text-field
                                  v-model="data.loadingStations"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.loading-stations')"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  type="number"
                                  tabindex="43"
                          />


                          <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.transport') }}</h5>
                          <v-row>
                              <v-col cols="12" lg="6" md="6" sm="12" xs="12">
                                  <v-checkbox
                                          v-model="data.transportPerson"
                                          :label="$t('book/technical-info:labels.transport-person')"
                                          color="secondary"
                                          tabindex="44"
                                  />
                                  <v-checkbox
                                          v-model="data.transportFreight"
                                          :label="$t('book/technical-info:labels.transport-freight')"
                                          color="secondary"
                                          tabindex="45"
                                  />
                              </v-col>
                              <v-col cols="12" lg="6" md="6" sm="12" xs="12">
                                  <v-checkbox
                                          v-model="data.transportCar"
                                          :label="$t('book/technical-info:labels.transport-car')"
                                          color="secondary"
                                          tabindex="46"
                                  />
                                  <v-checkbox
                                          v-model="data.transportBed"
                                          :label="$t('book/technical-info:labels.transport-bed')"
                                          color="secondary"
                                          tabindex="47"
                                  />
                              </v-col>
                          </v-row>
                      </v-col>
              <v-col cols="12" md="5">

                  <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.shaft') }}</h5>
                  <v-select
                          v-model="data.shaftProtectionType"
                          outlined
                          :label="$t('book/technical-info:labels.shaft-protection-type')"
                          :menu-props="{ bottom: true, offsetY: true }"
                          :items="restProtectionType"
                          clearable
                          item-value="name"
                          item-text="translatedName"
                          tabindex="48"
                  >
                  </v-select>
                  <h6 class="primary--text mb-5">{{ $t('book/technical-info:titles.shaft-door') }}</h6>
                  <v-select
                          v-model="data.shaftDoorType"
                          outlined
                          :label="$t('book/technical-info:labels.shaft-door-type')"
                          :menu-props="{ bottom: true, offsetY: true }"
                          :items="restDoorType"
                          clearable
                          item-value="name"
                          item-text="translatedName"
                          tabindex="49"
                  >
                  </v-select>

                  <v-text-field
                          v-model="data.shaftDoorWidth"
                          outlined
                          clearable
                          type="number"
                          :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                          :label="$t('book/technical-info:labels.shaft-door-width')"
                          tabindex="50"
                  />

                  <v-text-field
                          v-model="data.shaftDoorHeight"
                          outlined
                          clearable
                          type="number"
                          :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                          :label="$t('book/technical-info:labels.shaft-door-height')"
                          tabindex="51"
                  />
              </v-col>
                  </v-row>
              </v-tab-item>
            <v-tab-item
                value="emergency"
                class="pa-3 pa-sm-5"
                eager
            >
              <!-- Emergency Section -->
              <v-select
                  v-model="data.emergencyType"
                  outlined
                  :label="$t('book/technical-info:labels.emergency-type')"
                  :menu-props="{ bottom: true, offsetY: true }"
                  :items="restEmergencyType"
                  clearable
                  multiple
                  chips
                  item-text="translatedName"
                  item-value="name"
                  deletable-chips
                  tabindex="11"
              >
              </v-select>

              <v-checkbox
                  v-model="data.emergencyCallSystemGSM"
                  :label="$t('book/technical-info:labels.emergency-GSM')"
                  color="secondary"
                  tabindex="12"
              />

              <v-checkbox
                  v-model="data.emergencyAnalog"
                  :label="$t('book/technical-info:labels.emergency-analog')"
                  color="secondary"
                  tabindex="13"
              />

              <v-text-field
                  v-if="data.emergencyCallSystemGSM || data.emergencyAnalog"
                  v-model="data.emergencyPhoneNumber"
                  outlined
                  clearable
                  :label="$t('book/technical-info:labels.emergency-phone')"
                  tabindex="14"
              />

              <v-textarea
                  v-if="data.emergencyCallSystemGSM || data.emergencyAnalog"
                  v-model="data.emergencyCallSystemInfo"
                  outlined
                  clearable
                  :label="$t('book/technical-info:labels.emergency-info')"
                  tabindex="15"
              />
            </v-tab-item>

            <v-tab-item value="fire-control">
                  <v-row justify="space-between">
                    <v-col cols="12" md="5">
                      <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.fire-control') }}</h5>
                    </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <v-checkbox
                            v-model="data.fireControlPresent"
                            :label="$t('book/technical-info:labels.fire-control-present')"
                            color="secondary"
                            tabindex="46" />
                          <v-select
                            v-if="data.fireControlPresent"
                            v-model="data.fireControlType"
                            outlined
                            :label="$t('book/technical-info:labels.fire-control-type')"
                            :menu-props="{ bottom: true, offsetY: true }"
                            :items="restFireControlTypes"
                            clearable
                            item-value="name"
                            item-text="translatedName"
                            tabindex="47"
                          />
                          <v-checkbox
                            v-model="data.fireAlarmControlUnitPresent"
                            :label="$t('book/technical-info:labels.fire-alarm-control-unit-present')"
                            color="secondary"
                            tabindex="48"
                          />
                          <v-textarea
                            v-model="data.firefighterFreeText"
                            auto-grow
                            outlined
                            clearable
                            :label="$t('book/technical-info:labels.fire-control-free-text')"
                            tabindex="49"
                          />
                      </v-col>
                      <v-col cols="12" md="5">
                        <v-checkbox
                          v-model="data.firefighterLift"
                          :label="$t('book/technical-info:labels.firefighters-lift')"
                          color="secondary"
                          tabindex="50"
                        />
                        <v-checkbox
                          v-model="data.backupPower"
                          :label="$t('book/technical-info:labels.backup-power')"
                          color="secondary"
                          tabindex="51"
                        />
                      </v-col>
                  </v-row>
              </v-tab-item>
              <v-tab-item value="escalator" eager>
                  <!-- Technical information Section -->
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.tech') }}</h5>
                      </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <v-text-field
                                  v-model="data.operatingSpeed"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.operating-speed')"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  type="number"
                                  tabindex="52"
                          />

                          <v-text-field
                                  v-model="data.control"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.controls')"
                                  tabindex="53"
                          />

                          <v-text-field
                                  v-model="data.frequencyConverter"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.frequency-converter')"
                                  tabindex="54"
                          />

                      </v-col>
                      <v-col cols="12" md="5">

                          <CustomerTypeAhead
                                  ref="manufacturerTypeahead2"
                                  :value="data.manufacturer"
                                  @input="data.manufacturer = $event"
                                  :label="$t('book/technical-info:labels.manufacturer')"
                                  tabindex="55"
                          />

                          <v-text-field
                                  v-model="data.unknownManufacturer"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.other-manufacturer')"
                                  tabindex="56"
                          />

                          <v-text-field
                                  v-model="data.power"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.power')"
                                  tabindex="57"
                          />
                      </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col md="5" cols="12">

                          <v-checkbox
                                  v-model="data.energySavingOptionPresent"
                                  :label="$t('book/technical-info:labels.energy-saving-option-present')"
                                  color="secondary"
                                  tabindex="58"
                          />

                          <v-text-field
                                  v-if="data.energySavingOptionPresent"
                                  v-model="data.energySavingOption"
                                  outlined
                                  clearable
                                  :label="$t('book/technical-info:labels.energy-saving-option')"
                                  tabindex="59"
                          />
                      </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <h5 class="primary--text mb-5">
                              {{
                              escalatorTabActive ?
                                  $t('book/technical-info:titles.escalator') :
                                  $t('book/technical-info:titles.walkway')
                              }}
                          </h5>
                      </v-col>
                  </v-row>
                  <v-row justify="space-between">
                      <v-col cols="12" md="5">
                          <v-text-field
                                  v-model="data.inclination"
                                  outlined
                                  clearable
                                  type="number"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  :label="$t('book/technical-info:labels.inclination')"
                                  tabindex="60"
                          />

                          <v-select
                                  v-show="escalatorTabActive"
                                  v-model="data.conveyingDirection"
                                  outlined
                                  :label="$t('book/technical-info:labels.conveying-direction')"
                                  :menu-props="{ bottom: true, offsetY: true }"
                                  :items="restConveyingDirections"
                                  clearable
                                  item-value="name"
                                  item-text="translatedName"
                                  tabindex="61"
                          />
                          <v-text-field
                                  v-show="escalatorTabActive"
                                  v-model="data.escalatorElevation"
                                  outlined
                                  clearable
                                  type="number"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  :label="$t('book/technical-info:labels.escalator-elevation')"
                                  tabindex="62"
                          />

                          <v-text-field
                                  v-model="data.escalatorBearingHeight"
                                  outlined
                                  clearable
                                  type="number"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  :label="$t('book/technical-info:labels.escalator-bearing-height')"
                                  tabindex="63"
                          />
                      </v-col>
                      <v-col cols="12" md="5">
                          <v-text-field
                                  v-model="data.escalatorStepWidth"
                                  outlined
                                  clearable
                                  type="number"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  :label="$t('book/technical-info:labels.escalator-step-width')"
                                  tabindex="64"
                          />

                          <v-text-field
                                  v-model="data.escalatorWidth"
                                  outlined
                                  clearable
                                  type="number"
                                  :rules="[ruleNotNegative($t('utils:rules.negative-value'))]"
                                  :label="$t('book/technical-info:labels.escalator-width')"
                                  tabindex="65"
                          />

                          <v-checkbox
                                  v-model="data.alternatingDirections"
                                  :label="$t('book/technical-info:labels.alternating-directions')"
                                  color="secondary"
                                  tabindex="66"
                          />

                          <v-checkbox
                                  v-model="data.escalatorStepLighting"
                                  :label="$t('book/technical-info:labels.escalator-step-light')"
                                  color="secondary"
                                  tabindex="67"
                          />
                      </v-col>
                  </v-row>

                  <div v-show="escalatorTabActive">
                      <h5 class="primary--text mb-5">{{ $t('book/technical-info:titles.story') }}</h5>
                      <v-row>
                          <v-col cols="12" lg="6" md="6" sm="12" xs="12">
                              <v-text-field
                                      v-model="data.laneStart"
                                      outlined
                                      clearable
                                      :label="$t('book/technical-info:labels.lane-start')"
                                      tabindex="68"
                              />
                          </v-col>
                          <v-col cols="12" lg="6" md="6" sm="12" xs="12">
                              <v-text-field
                                      v-model="data.laneEnd"
                                      outlined
                                      clearable
                                      :label="$t('book/technical-info:labels.lane-end')"
                                      tabindex="69"
                              />
                          </v-col>
                      </v-row>
                  </div>

              </v-tab-item>
        </v-tabs-items>
        <v-row justify="space-between">
          <v-col cols="12" md="5">
            <BaseButton
                color="primary"
                :btnText="$t('buttons:save-changes')"
                @clickedButton="updateForm()"
                tabindex="199"
            />
          </v-col>
        </v-row>
      </v-card>
    </v-form>
  </div>
</template>
<script>

import InfoService from "@/modules/core/services/info-service";
import TechnicalInfoService from "@/modules/book/services/technical-info-service";

import {ruleNotNegative} from "@/modules/core/services/rules";
import DataUtils from "@/modules/core/services/data-utils";
import Utils from "@/modules/core/services/utils";

import TechnicalInfo from "@/modules/book/data/technical-info";

import DateField from '@/modules/core/components/DateField';
import CustomerTypeAhead from "@/modules/core/components/CustomerTypeAhead";
import BaseButton from "@/modules/core/components/Base/BaseButton";
import EnumService from "@/modules/core/services/enum-service";
import FacilityService from "@/modules/book/services/facility-service";
import QRCode from "@/modules/book/data/qrcode";
import QRCodeService from "@/modules/book/services/qrcode-service";
import Facility from "@/modules/book/data/facility";
import {mapGetters} from "vuex";


export default {
  name: 'FacilityTechnicalInfoTab',
  components: {
    DateField,
    CustomerTypeAhead,
    BaseButton
  },
  data() {
    return {
      data: new TechnicalInfo(),
      code: new QRCode(),
      restElevatorType: [],
      restDrives: [],
      restEngineRoomPositions: [],
      restDoorPositions: [],
      restDoorSecurityType: [],
      restDoorType: [],
      restEmergencyType: [],
      restHydraulicDriveType: [],
      restRopeDriveType: [],
      restProtectionType: [],
      restFacilities: [],
      restConveyingDirections: [],
      restGlassDoorTypes: [],
      restFireControlTypes: [],
      facilitySearch: '',
      currentTab: '',
      isFetchingInfo: true
    }
  },
  methods: {
    ruleNotNegative,
    updateForm() {
      if (!this.$refs.form.validate()) {
        Utils.showWarningSnackbar(this.$t('snackbar:empty-fields'))

        window.scrollTo(0, 0);
        return;
      }

      TechnicalInfoService.putToAPI(this.$route.params.id, this.data,
        () => {
          Utils.showSuccessSnackbar(this.$t('snackbar:edit-success', {name: this.$t('book/technical-info:snackbar.documentation')}));
        },
        () => {
          Utils.showFailureSnackbar(this.$t('snackbar:edit-failure', {name: this.$t('book/technical-info:snackbar.documentation')}))
        }
      )
    },

    getSubDrive(drive) {
      if (drive === 'HYDRAULIC') {
        this.getEnum(
            "elevator/drive/hydraulic",
            "enums:technical.elevator-hydraulic-drive-types.",
            this.restHydraulicDriveType,
            "elevator hydraulic drive types",
            "hydraulic"
        )
      } else if (drive === 'ROPE') {
        this.getEnum(
            "elevator/drive/rope",
            "enums:technical.elevator-rope-drive-types.",
            this.restRopeDriveType,
            "elevator rope drive types",
            "rope"
        )
      }
    },

    /**
     * Generic method the read specific enums
     *
     * @param enumPath api path of the enum e.g. 'elevator/drive/rope'
     * @param translationPath path of the translation of the enum e.g. 'enums:technical.elevator-rope-drive-types.'
     * @param enumArray data array, where read, translated and sorted enums should be stored
     * @param failureString part of the error string for backend logger e.g. 'elevator rope drive types'
     * @param failureTranslationPath translation path for failure snackbar
     */
    getEnum(enumPath, translationPath, enumArray, failureString, failureTranslationPath) {
      InfoService.readAndTranslateEnum(
          enumPath,
          translationPath,
          (res) => {
            res.forEach((element) => {
              enumArray.push(element)
            })
          },
          (error) => {
            this.$logger.error("Failed to read " + failureString + " , because: " + error);
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('book/technical-info:snackbar. ' + failureTranslationPath)}))
          }
      )
    }
  },
  computed: {
    ...mapGetters('book/layout', ['facilityInfoTabs']),
    elevatorTabActive() {
      if (!Array.isArray(this.facilityInfoTabs)) {
        return false
      }
      return this.facilityInfoTabs.includes('elevator')
    },
    escalatorTabActive() {
      if (!Array.isArray(this.facilityInfoTabs)) {
        return false
      }
      return this.facilityInfoTabs.includes('escalator')
    },
    walkwayTabActive() {
      if (!Array.isArray(this.facilityInfoTabs)) {
        return false
      }
      return this.facilityInfoTabs.includes('walkway')
    },
    escalatorOrWalkway() {
      if (!Array.isArray(this.facilityInfoTabs)) {
        return false
      }
      return this.facilityInfoTabs.includes('escalator') || this.facilityInfoTabs.includes('walkway')
    },
    currentLang() {
      return this.$store.state.translation.language;
    },

    getShaftMineWarningText() {
      if (this.data.reducedShaftHead && !this.data.reducedShaftPit) {
        return this.$t('book/technical-info:shaft-mine.only-head');
      } else if (!this.data.reducedShaftHead && this.data.reducedShaftPit) {
        return this.$t('book/technical-info:shaft-mine.only-pit');
      } else if (this.data.reducedShaftHead && this.data.reducedShaftPit) {
        return this.$t('book/technical-info:shaft-mine.head-and-pit');
      } else {
        return ''
      }
    }
  },
  watch: {
    currentLang() {
      Utils.sleep(500).then(() => {
        this.$refs.form.resetValidation()

        // translate and sort enums
        EnumService.translateAndSortEnumObject(this.restElevatorType, "enums:technical.elevator-type.")
        EnumService.translateAndSortEnumObject(this.restDrives, "enums:technical.drive.")
        EnumService.translateAndSortEnumObject(this.restEngineRoomPositions, "enums:technical.engine-room-position.")
        EnumService.translateAndSortEnumObject(this.restDoorPositions, "enums:technical.elevator-door-position.")
        EnumService.translateAndSortEnumObject(this.restDoorSecurityType, "enums:technical.elevator-door-security-types.")
        EnumService.translateAndSortEnumObject(this.restDoorType, "enums:technical.elevator-door-types.")
        EnumService.translateAndSortEnumObject(this.restEmergencyType, "enums:technical.elevator-emergency-types.")
        EnumService.translateAndSortEnumObject(this.restHydraulicDriveType, "enums:technical.elevator-hydraulic-drive-types.")
        EnumService.translateAndSortEnumObject(this.restRopeDriveType, "enums:technical.elevator-rope-drive-types.")
        EnumService.translateAndSortEnumObject(this.restProtectionType, "enums:technical.elevator-protection-types.")
        EnumService.translateAndSortEnumObject(this.restConveyingDirections, "enums:technical.escalator-conveying-directions.")
        EnumService.translateAndSortEnumObject(this.restGlassDoorTypes, "enums:technical.glass-door-types.")
        EnumService.translateAndSortEnumObject(this.restFireControlTypes, "enums:technical.fire-control-types.")
      });
    },

    //if specific emergencyType enums are selected
    //set the according flag
    'data.emergencyType' (val) {
      if (val && val.includes('GSM_EMERGENCY_CALL_SYSTEM')) {
        this.data.emergencyCallSystemGSM = true;
      } else {
        this.data.emergencyCallSystemGSM = false;
      }

      if (val && val.includes('ANALOG_SYSTEM')) {
        this.data.emergencyAnalog = true;
      } else {
        this.data.emergencyAnalog = false;
      }

      if (val && val.includes('AWM_MODULE')) {
        this.data.awmModule = true;
      } else {
        this.data.awmModule = false;
      }
    },

    'data.emergencyCallSystemGSM' (val) {
      if (val && this.data.emergencyType && !this.data.emergencyType.includes('GSM_EMERGENCY_CALL_SYSTEM')) {
        this.data.emergencyType.push('GSM_EMERGENCY_CALL_SYSTEM')
      } else if (!val && this.data.emergencyType) {
        for (let i = this.data.emergencyType.length - 1; i >= 0 ; i--) {
          if (this.data.emergencyType[i] === 'GSM_EMERGENCY_CALL_SYSTEM') {
            this.data.emergencyType.splice(i, 1);
          }
        }
      }
    },

    'data.emergencyAnalog' (val) {
      if (val && this.data.emergencyType && !this.data.emergencyType.includes('ANALOG_SYSTEM')) {
        this.data.emergencyType.push('ANALOG_SYSTEM')
      } else if (!val && this.data.emergencyType) {
        for (let i = this.data.emergencyType.length - 1; i >= 0 ; i--) {
          if (this.data.emergencyType[i] === 'ANALOG_SYSTEM') {
            this.data.emergencyType.splice(i, 1);
          }
        }
      }
    },

    'data.awmModule' (val) {
      if (val && this.data.emergencyType && !this.data.emergencyType.includes('AWM_MODULE')) {
        this.data.emergencyType.push('AWM_MODULE')
      } else if (!val && this.data.emergencyType) {
        for (let i = this.data.emergencyType.length - 1; i >= 0 ; i--) {
          if (this.data.emergencyType[i] === 'AWM_MODULE') {
            this.data.emergencyType.splice(i, 1);
          }
        }
      }
    }
  },
  created() {
    if (!this.$route.params.id) {
      this.$router.push('/404');
    }

    FacilityService.readFromAPI(this.$route.params.id,
        (res) => {
          if (res.data.qrcode) {
            QRCodeService.readFromAPI(res.data.qrcode,
                (resp) => {
                  this.code = DataUtils.parseDataToSingleObject(resp.data, QRCode);
                }
            )
          }
        }
    )

    FacilityService.readAllFromAPI(
        (res) => {
          this.restFacilities = DataUtils.parseDataToObjectArray(res.data, Facility);

          // remove current facility from list
          for (let i = this.restFacilities.length - 1; i >= 0; i--) {
            if (this.restFacilities[i].id === this.$route.params.id) {
              this.restFacilities.splice(i, 1);
            }
          }

          this.restFacilities = Utils.sort(this.restFacilities, false, 'name');
        },
        (error) => {
          this.$logger.error("Could not load facilities, because: " + error);
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:facility_plural')}));
        }
    )

    TechnicalInfoService.readFromAPI(this.$route.params.id,
      (resp) => {
        let technicalInfo = DataUtils.parseDataToSingleObject(resp.data, TechnicalInfo);
        if (technicalInfo == null) {
          this.$router.push('/404');
          return;
        }
        this.data = technicalInfo;

        // initialise CustomerTypeAheads after ids are available
        if (this.$refs.assemblerTypeahead) {
          this.$refs.assemblerTypeahead.initialise(this.data.assembler);
        }
        if (this.$refs.manufacturerTypeahead1) {
          this.$refs.manufacturerTypeahead1.initialise(this.data.manufacturer);
        }
        if (this.$refs.manufacturerTypeahead2) {
          this.$refs.manufacturerTypeahead2.initialise(this.data.manufacturer);
        }

        //format dates
        if (this.data.buildingPermit) {
          this.data.buildingPermit = this.$refs.datePermit.parseDTO(this.data.buildingPermit)
        }

        if (this.data.permissionToUse) {
          this.data.permissionToUse = this.$refs.datePermission.parseDTO(this.data.permissionToUse)
        }

        if (this.data.modernizedOn) {
          this.data.modernizedOn = this.$refs.dateModernized.parseDTO(this.data.modernizedOn);
        }

        if (this.data.warrantyEnd) {
          this.data.warrantyEnd = this.$refs.warrantyEnd.parseDTO(this.data.warrantyEnd);
        }

        if (this.data.emergencyCallSystemGSM && !this.data.emergencyType.includes('GSM_EMERGENCY_CALL_SYSTEM')) {
          this.data.emergencyType.push('GSM_EMERGENCY_CALL_SYSTEM')
        }

        if (this.data.emergencyAnalog && !this.data.emergencyType.includes('ANALOG_SYSTEM')) {
          this.data.emergencyType.push('ANALOG_SYSTEM')
        }

        if (this.data.awmModule && !this.data.emergencyType.includes('AWM_MODULE')) {
          this.data.emergencyType.push('AWM_MODULE')
        }

        // read elevator types
        this.getEnum(
            "elevator/type",
            "enums:technical.elevator-type.",
            this.restElevatorType,
            "elevator types",
            "elevator-type"
        )

        // read drive types
        this.getEnum(
            "elevator/drive",
            "enums:technical.drive.",
            this.restDrives,
            "elevator drive types",
            "drive"
        )

        // read engine room position types
        this.getEnum(
            "elevator/engine",
            "enums:technical.engine-room-position.",
            this.restEngineRoomPositions,
            "elevator engine types",
            "engine-room-position"
        )

        // read door position types
        this.getEnum(
            "elevator/door/position",
            "enums:technical.elevator-door-position.",
            this.restDoorPositions,
            "elevator door position types",
            "door-position"
        )

        // read door security types
        this.getEnum(
            "elevator/door/security",
            "enums:technical.elevator-door-security-types.",
            this.restDoorSecurityType,
            "elevator door security types",
            "security-type"
        )

        // read door types
        this.getEnum(
            "elevator/door/type",
            "enums:technical.elevator-door-types.",
            this.restDoorType,
            "elevator door types",
            "door-type"
        )

        // read emergency types
        this.getEnum(
            "elevator/emergency",
            "enums:technical.elevator-emergency-types.",
            this.restEmergencyType,
            "elevator emergency types",
            "emergency"
        )

        // read emergency types
        this.getEnum(
            "elevator/protection",
            "enums:technical.elevator-protection-types.",
            this.restProtectionType,
            "elevator protection types",
            "protection-type"
        )

        // hydraulic drive types
        this.getEnum(
            "elevator/drive/hydraulic",
            "enums:technical.elevator-hydraulic-drive-types.",
            this.restHydraulicDriveType,
            "elevator hydraulic drive types",
            "hydraulic"
        )

        // rope drive types
        this.getEnum(
            "elevator/drive/rope",
            "enums:technical.elevator-rope-drive-types.",
            this.restRopeDriveType,
            "elevator rope drive types",
            "rope"
        )

        // escalator conveying directions
        this.getEnum(
            "escalator/conveying-directions",
            "enums:technical.escalator-conveying-directions.",
            this.restConveyingDirections,
            "escalator conveying directions",
            "conveying-directions"
        )

        // glass door types
        this.getEnum(
            "elevator/glass-door",
            "enums:technical.glass-door-types.",
            this.restGlassDoorTypes,
            "glass door types",
            "glass-door-types"
        )

        // fire control types
        this.getEnum(
            "elevator/fire-control",
            "enums:technical.fire-control-types.",
            this.restFireControlTypes,
            "fire control types",
            "fire-control-types"
        )
        this.isFetchingInfo = false;
      },
      (error) => {
        this.$logger.error(error);
        this.$router.push('/404');
      }
    )
  }
}
</script>
<style lang="scss">
.facility-technical-info-tab {
  .v-expansion-panel::before {
    box-shadow: 0 2px 4px 0 #e8eafc, 0 1px 1px -2px #b2b2b21a, 0 1px 1px 0 #9a9a9a1a;
  }

  .v-expansion-panel:not(:first-child)::after {
    border-top: none;
  }

  .facility-autocomplete {
    .v-select__selections {
      .v-chip.v-size--default {
        min-height: 32px;
        height: 100%!important;
      }
      .v-chip {
        white-space: normal;
      }
    }
  }
}
</style>